import axios, {
  type AxiosError,
  type AxiosInstance,
  type InternalAxiosRequestConfig,
  type AxiosResponse,
} from 'axios';

import { activeUnitForUser, rotateLoginEpoch } from './crm-active-unit';

// API Client Configuration
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5010/api';
const API_TIMEOUT = 30000; // 30 seconds

// Token management
const TOKEN_KEY = 'access_token';
const REFRESH_TOKEN_KEY = 'refresh_token';
const MAX_AUTHORIZATION_HEADER_LENGTH = 8192;

// Proactive refresh: refresh when 80% of token lifetime has elapsed
// This prevents 401s by refreshing before expiry (Google/Microsoft pattern)
const TOKEN_REFRESH_THRESHOLD = 0.8;

const PUBLIC_AUTH_ENDPOINT_PATTERN =
  /\/auth\/(captcha\/config|email\/verify|forgot-password|login|magic-link\/(request|send|verify)|mfa\/(challenge|enrollment\/(setup|verify))|oauth\/(init|callback)|passkey\/authenticate\/(options|verify)|public-config|refresh|reset-password|superadmin\/login|tenant\/signup|verify\/(email|email\/resend|company-email|company-email\/resend|phone|phone\/resend|status))$/;

export const isPublicAuthEndpoint = (url: string | undefined): boolean => {
  if (!url) return false;
  const path = url.startsWith(API_BASE_URL) ? url.slice(API_BASE_URL.length) : url;
  return PUBLIC_AUTH_ENDPOINT_PATTERN.test(path.split('?')[0] ?? path);
};

export const isCrmEndpoint = (url: string | undefined): boolean => {
  if (!url) return false;
  const path = url.startsWith(API_BASE_URL) ? url.slice(API_BASE_URL.length) : url;
  const pathname = path.split('?')[0] ?? path;
  return pathname === '/crm' || pathname.startsWith('/crm/');
};

const normalizeToken = (token: string | null | undefined): string | null => {
  const trimmed = token?.trim();
  if (!trimmed || trimmed === 'undefined' || trimmed === 'null') {
    return null;
  }
  return trimmed;
};

export const getAccessToken = (): string | null => {
  const impersonationToken = normalizeToken(sessionStorage.getItem('impersonation_token'));
  if (impersonationToken) return impersonationToken;
  return normalizeToken(localStorage.getItem(TOKEN_KEY));
};

export const getRefreshToken = (): string | null => {
  return normalizeToken(localStorage.getItem(REFRESH_TOKEN_KEY));
};

export const isAccessTokenOversized = (token: string | null | undefined): boolean => {
  const normalizedToken = normalizeToken(token);
  if (!normalizedToken) return false;

  return `Bearer ${normalizedToken}`.length > MAX_AUTHORIZATION_HEADER_LENGTH;
};

// Internal token write, WITHOUT starting a new login session. Used by the
// silent-refresh paths only — a refresh continues the same session, and must
// not invalidate the user's per-session workspace pick.
const storeTokens = (accessToken: string, refreshToken: string): void => {
  const normalizedAccessToken = normalizeToken(accessToken);
  const normalizedRefreshToken = normalizeToken(refreshToken);

  if (!normalizedAccessToken || !normalizedRefreshToken) {
    clearTokens();
    throw new Error('Cannot persist empty authentication tokens');
  }

  localStorage.setItem(TOKEN_KEY, normalizedAccessToken);
  localStorage.setItem(REFRESH_TOKEN_KEY, normalizedRefreshToken);
};

/**
 * Install a session's tokens. Every genuine session start funnels through this
 * one exported function (password login, MFA verify, SSO, magic link, passkey,
 * impersonation switch/restore — both auth service modules re-export it), so
 * rotating the CRM login epoch here means "ask the workspace choice once per
 * login" can never silently miss a login path. The refresh paths deliberately
 * bypass it via storeTokens — otherwise the chooser would reappear on every
 * silent token refresh.
 */
export const setTokens = (accessToken: string, refreshToken: string): void => {
  storeTokens(accessToken, refreshToken);
  rotateLoginEpoch();
};

export const clearTokens = (): void => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  sessionStorage.removeItem('impersonation_token');
  sessionStorage.removeItem('impersonating');
  sessionStorage.removeItem('original_access_token');
  sessionStorage.removeItem('original_refresh_token');
};

// --- Impersonation Support ---
export const impersonation = {
  isImpersonating: (): boolean => !!sessionStorage.getItem('impersonation_token'),

  setImpersonationToken: (token: string): void => {
    const impersonationToken = normalizeToken(token);
    if (!impersonationToken) {
      throw new Error('Cannot impersonate with an empty authentication token');
    }

    const originalAccess = getAccessToken();
    const originalRefresh = getRefreshToken();
    if (originalAccess && !sessionStorage.getItem('original_access_token')) {
      sessionStorage.setItem('original_access_token', originalAccess);
    }
    if (originalRefresh && !sessionStorage.getItem('original_refresh_token')) {
      sessionStorage.setItem('original_refresh_token', originalRefresh);
    }
    sessionStorage.setItem('impersonation_token', impersonationToken);
    sessionStorage.setItem('impersonating', 'true');
  },

  clearImpersonation: (): void => {
    sessionStorage.removeItem('impersonation_token');
    sessionStorage.removeItem('impersonating');
  },

  restoreOriginalSession: (): boolean => {
    const originalAccess = normalizeToken(sessionStorage.getItem('original_access_token'));
    const originalRefresh = normalizeToken(sessionStorage.getItem('original_refresh_token'));
    if (originalAccess && originalRefresh) {
      setTokens(originalAccess, originalRefresh);
      sessionStorage.removeItem('original_access_token');
      sessionStorage.removeItem('original_refresh_token');
      sessionStorage.removeItem('impersonation_token');
      sessionStorage.removeItem('impersonating');
      return true;
    }
    return false;
  },

  getOriginalToken: (): string | null =>
    normalizeToken(sessionStorage.getItem('original_access_token')),
};

// Callback for full logout (clears both tokens and store)
// Set by the auth store initialization
let onAuthFailure: (() => void) | null = null;

export const setAuthFailureHandler = (handler: () => void): void => {
  onAuthFailure = handler;
};

// Callback for network errors (server unreachable)
// Lets consumers react to connectivity issues without triggering logout
let onNetworkError: (() => void) | null = null;

export const setNetworkErrorHandler = (handler: () => void): void => {
  onNetworkError = handler;
};

export const triggerAuthFailure = (): void => {
  clearTokens();
  if (onAuthFailure) {
    onAuthFailure();
  }
};

// JWT payload type
export interface JwtPayload {
  sub: string;
  tenantId: string;
  tenantSlug: string;
  roles: string[];
  permissions?: string[];
  email: string;
  jti: string;
  iat: number;
  exp: number;
  // Org context - populated from Employee record
  employeeId?: string;
  departmentId?: string;
  positionId?: string;
  isChiefPosition?: boolean;
  teamIds?: string[];
  managedTeamIds?: string[];
  managedDepartmentId?: string;
}

/**
 * Decode a JWT token and extract the payload
 * Note: This does NOT verify the signature, just decodes the payload
 */
export const decodeJwt = (token: string): JwtPayload | null => {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;

    const payload = parts[1];
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decoded) as JwtPayload;
  } catch {
    return null;
  }
};

/**
 * Check if a token needs proactive refresh (80% of lifetime elapsed)
 */
const isTokenNearExpiry = (token: string): boolean => {
  const payload = decodeJwt(token);
  if (!payload?.exp || !payload?.iat) return true;

  const now = Math.floor(Date.now() / 1000);
  const lifetime = payload.exp - payload.iat;
  const elapsed = now - payload.iat;

  return elapsed >= lifetime * TOKEN_REFRESH_THRESHOLD;
};

// API Error Response Type
export interface ApiErrorResponse {
  message: string;
  statusCode: number;
  error?: string;
  errors?: Record<string, string[]>;
}

/**
 * R30-027 — Cross-tab refresh coordinator.
 *
 * Without coordination, every tab fires its own `/auth/refresh` when the
 * access token nears expiry. The server-side advisory lock now serialises
 * these (so they're correct), but firing 4-5 concurrent refreshes still:
 * (a) wastes server work, (b) the loser tabs see `REFRESH_REUSED` and
 * hard-logout the user — that's the correct behaviour but a poor UX when
 * the "attacker" is just another tab of the same browser.
 *
 * Coordinator design:
 * - One BroadcastChannel('unitree-auth-refresh') shared across tabs.
 * - First tab that needs a refresh broadcasts REFRESH_START with a request
 *   id. Other tabs receive REFRESH_START and KNOW a refresh is in flight —
 *   they don't fire their own.
 * - Leader writes REFRESH_DONE with the new tokens (also persisted to
 *   localStorage by `setTokens` — followers observe the storage event and
 *   pick up the new tokens). REFRESH_DONE explicitly resolves any waiting
 *   followers immediately (no 100ms storage-event lag).
 * - Leader writes REFRESH_FAILED on error — followers reject and fall
 *   through to their own attempt (server lock still protects).
 * - 5s timeout on follower wait — if the leader's tab crashes mid-refresh,
 *   followers don't hang forever.
 * - BroadcastChannel unsupported (older Safari) → degrades to per-tab
 *   refresh. Server-side advisory lock still ensures correctness.
 *
 * In-tab mutex is preserved INSIDE the coordinator so concurrent code paths
 * within the same tab (interceptor + proactive timer) collapse to one call.
 */
const REFRESH_CHANNEL_NAME = 'unitree-auth-refresh';
const REFRESH_FOLLOWER_TIMEOUT_MS = 5000;

type RefreshMessage =
  | { type: 'REFRESH_START'; requestId: string; ts: number }
  | {
      type: 'REFRESH_DONE';
      requestId: string;
      accessToken: string;
      refreshToken: string;
      ts: number;
    }
  | { type: 'REFRESH_FAILED'; requestId: string; reason: string; ts: number };

class RefreshCoordinator {
  private channel: BroadcastChannel | null = null;
  private inFlightLocal: Promise<string> | null = null;
  // Set when ANY tab announces it has started a refresh. Followers await on this.
  private followerWait: {
    requestId: string;
    promise: Promise<string>;
    resolve: (token: string) => void;
    reject: (err: Error) => void;
    timeoutId: ReturnType<typeof setTimeout>;
  } | null = null;

  constructor() {
    try {
      this.channel = new BroadcastChannel(REFRESH_CHANNEL_NAME);
      this.channel.onmessage = (event) => this.handleMessage(event.data as RefreshMessage);
    } catch {
      // BroadcastChannel unsupported — degrade to per-tab. Server lock still wins.
      this.channel = null;
    }
  }

  private handleMessage(msg: RefreshMessage): void {
    if (msg.type === 'REFRESH_START') {
      // Another tab is refreshing. If we don't already have a local in-flight
      // call, register a follower wait so subsequent local callers piggyback.
      if (this.inFlightLocal !== null || this.followerWait !== null) return;
      this.registerFollowerWait(msg.requestId);
      return;
    }

    if (msg.type === 'REFRESH_DONE') {
      // Persist tokens (the leader has already done this on its side; this
      // covers the case where this tab is a follower and never wrote them).
      // storeTokens, not setTokens: a refresh continues the SAME session.
      storeTokens(msg.accessToken, msg.refreshToken);
      // Resolve any waiting followers in this tab.
      if (this.followerWait && this.followerWait.requestId === msg.requestId) {
        clearTimeout(this.followerWait.timeoutId);
        this.followerWait.resolve(msg.accessToken);
        this.followerWait = null;
      }
      return;
    }

    if (msg.type === 'REFRESH_FAILED') {
      if (this.followerWait && this.followerWait.requestId === msg.requestId) {
        clearTimeout(this.followerWait.timeoutId);
        this.followerWait.reject(new Error(`Leader tab failed: ${msg.reason}`));
        this.followerWait = null;
      }
      return;
    }
  }

  private registerFollowerWait(requestId: string): void {
    let resolveFn: (token: string) => void = () => undefined;
    let rejectFn: (err: Error) => void = () => undefined;
    const promise = new Promise<string>((resolve, reject) => {
      resolveFn = resolve;
      rejectFn = reject;
    });
    const timeoutId = setTimeout(() => {
      // Leader didn't respond in time — release follower so it can retry.
      if (this.followerWait?.requestId === requestId) {
        this.followerWait = null;
        rejectFn(new Error('Refresh leader timeout'));
      }
    }, REFRESH_FOLLOWER_TIMEOUT_MS);
    this.followerWait = { requestId, promise, resolve: resolveFn, reject: rejectFn, timeoutId };
  }

  /**
   * Coordinate a refresh call. If a leader is already running (in this tab
   * or another), wait on it. Otherwise become the leader.
   *
   * `doRefresh` is the actual network call — passed in to avoid coupling
   * the coordinator to axios.
   */
  async coordinate(
    doRefresh: () => Promise<{ accessToken: string; refreshToken: string }>,
  ): Promise<string> {
    // Same-tab concurrent callers piggyback on the in-flight promise.
    if (this.inFlightLocal !== null) return this.inFlightLocal;
    // Another tab is leading — wait on it.
    if (this.followerWait !== null) return this.followerWait.promise;

    const requestId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    if (this.channel) {
      const startMsg: RefreshMessage = { type: 'REFRESH_START', requestId, ts: Date.now() };
      this.channel.postMessage(startMsg);
    }

    this.inFlightLocal = (async () => {
      try {
        const tokens = await doRefresh();
        // storeTokens, not setTokens: a refresh continues the SAME session —
        // rotating the login epoch here would re-ask the workspace choice
        // every ~15 minutes instead of once per login.
        storeTokens(tokens.accessToken, tokens.refreshToken);
        if (this.channel) {
          const doneMsg: RefreshMessage = {
            type: 'REFRESH_DONE',
            requestId,
            accessToken: tokens.accessToken,
            refreshToken: tokens.refreshToken,
            ts: Date.now(),
          };
          this.channel.postMessage(doneMsg);
        }
        return tokens.accessToken;
      } catch (err) {
        if (this.channel) {
          const failMsg: RefreshMessage = {
            type: 'REFRESH_FAILED',
            requestId,
            reason: err instanceof Error ? err.message : 'unknown',
            ts: Date.now(),
          };
          this.channel.postMessage(failMsg);
        }
        throw err;
      } finally {
        this.inFlightLocal = null;
      }
    })();

    return this.inFlightLocal;
  }
}

const refreshCoordinator = new RefreshCoordinator();

class ApiClient {
  private client: AxiosInstance;
  private proactiveRefreshTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      timeout: API_TIMEOUT,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
    this.scheduleProactiveRefresh();
  }

  /**
   * Schedule proactive token refresh based on token expiry (Google/Microsoft pattern)
   * Refreshes at 80% of token lifetime — never waits for a 401
   */
  private scheduleProactiveRefresh(): void {
    if (impersonation.isImpersonating()) return; // Don't refresh impersonation tokens

    if (this.proactiveRefreshTimer) {
      clearTimeout(this.proactiveRefreshTimer);
      this.proactiveRefreshTimer = null;
    }

    const token = getAccessToken();
    if (!token) return;

    const payload = decodeJwt(token);
    if (!payload?.exp || !payload?.iat) return;

    const now = Math.floor(Date.now() / 1000);
    const lifetime = payload.exp - payload.iat;
    const refreshAt = payload.iat + Math.floor(lifetime * TOKEN_REFRESH_THRESHOLD);
    const delaySeconds = refreshAt - now;

    if (delaySeconds <= 0) {
      // Already past refresh threshold — refresh immediately
      void this.doAtomicRefresh();
      return;
    }

    this.proactiveRefreshTimer = setTimeout(() => {
      void this.doAtomicRefresh();
    }, delaySeconds * 1000);
  }

  /**
   * Atomic token refresh — coordinated across tabs via `RefreshCoordinator`
   * (R30-027) and serialised server-side by a Postgres advisory lock on the
   * refresh token's jti.
   */
  private doAtomicRefresh(): Promise<string> {
    return refreshCoordinator
      .coordinate(async () => {
        const refreshToken = getRefreshToken();
        if (!refreshToken) {
          throw new Error('No refresh token available');
        }
        const response = await this.client.post<{ accessToken: string; refreshToken: string }>(
          '/auth/refresh',
          { refreshToken },
        );
        return response.data;
      })
      .then((accessToken) => {
        // Schedule next proactive refresh for the new token
        this.scheduleProactiveRefresh();
        return accessToken;
      })
      .catch((refreshError) => {
        // R30-024: "No refresh token available" is an *expected* failure on
        // the unauthenticated-user path (user never logged in).
        const isNoRefreshToken =
          refreshError instanceof Error && refreshError.message === 'No refresh token available';

        const isNetworkError =
          !isNoRefreshToken &&
          refreshError instanceof Error &&
          (refreshError.message.includes('Network Error') ||
            refreshError.message.includes('timeout') ||
            !('response' in (refreshError as unknown as Record<string, unknown>)));

        const status = (refreshError as AxiosError)?.response?.status;
        const isAuthError = status === 401 || status === 403;

        // R30-027 — REFRESH_REUSED means the refresh token was replayed
        // (already-rotated jti). The server has already revoked the entire
        // user-token chain; clients MUST hard-logout with a distinct toast
        // so the user knows their session ended for SECURITY reasons, not
        // just expiry.
        const errorBody = (refreshError as AxiosError<{ code?: string }>)?.response?.data;
        const isRefreshReused = isAuthError && errorBody?.code === 'REFRESH_REUSED';

        if (isRefreshReused) {
          triggerAuthFailure();
          if (window.location.pathname !== '/login') {
            window.location.href = '/login?reason=session_compromised';
          }
        } else if (isAuthError) {
          triggerAuthFailure();
          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
        } else if (isNoRefreshToken) {
          // Expected on the pre-login path — quiet rejection.
        } else if (isNetworkError) {
          console.warn('Server appears to be down. Token refresh failed due to network error.');
          onNetworkError?.();
        } else {
          // Unknown error (e.g. 500, parsing error) — do NOT trigger auth failure.
          console.warn('Token refresh failed with unexpected error:', refreshError);
        }

        throw refreshError;
      });
  }

  private setupInterceptors(): void {
    // Request Interceptor — proactive refresh before sending if token is near expiry
    this.client.interceptors.request.use(
      async (config: InternalAxiosRequestConfig) => {
        // Skip auth and stale tenant headers for public auth endpoints. Login
        // requests must not inherit an old oversized JWT, otherwise local Vite
        // proxy / gateway rejects them with 431 before credentials are checked.
        const publicAuthEndpoint = isPublicAuthEndpoint(config.url);

        const token = getAccessToken();

        if (config.headers && publicAuthEndpoint) {
          delete config.headers.Authorization;
          delete config.headers['X-Tenant-Id'];
        } else if (token && isAccessTokenOversized(token) && config.headers) {
          clearTokens();
          localStorage.removeItem('tenant_id');
          delete config.headers.Authorization;
          delete config.headers['X-Tenant-Id'];
        } else if (token && config.headers) {
          // Proactive refresh: if token is past 80% lifetime, refresh BEFORE sending
          if (!impersonation.isImpersonating() && isTokenNearExpiry(token)) {
            try {
              const newToken = await this.doAtomicRefresh();
              config.headers.Authorization = `Bearer ${newToken}`;
            } catch {
              // Refresh failed — send with current token, let 401 handler deal with it
              config.headers.Authorization = `Bearer ${token}`;
            }
          } else {
            config.headers.Authorization = `Bearer ${token}`;
          }
        }

        // Add tenant ID if available (for multi-tenant support)
        const tenantId = localStorage.getItem('tenant_id');
        if (tenantId && config.headers && !publicAuthEndpoint) {
          config.headers['X-Tenant-Id'] = tenantId;
        }

        // BU-v2 P3-3: the CRM "create into" operating unit. Sent only for the
        // user it was chosen by (shared-machine safe); the CRM service
        // re-validates it against the caller's reach before using it.
        if (token && config.headers && isCrmEndpoint(config.url)) {
          const activeUnit = activeUnitForUser(decodeJwt(token)?.sub);
          if (activeUnit) {
            config.headers['X-CRM-Active-Unit'] = activeUnit;
          }
        }

        return config;
      },
      (error) => {
        return Promise.reject(error);
      },
    );

    // Response Interceptor — reactive fallback for any 401s that slip through
    this.client.interceptors.response.use(
      (response: AxiosResponse) => response,
      async (error: AxiosError<ApiErrorResponse>) => {
        const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

        // R30-024: public auth endpoints (login / register / refresh /
        // forgot-password / reset-password / verify) legitimately return 401
        // on bad credentials. Trying to refresh in that case is nonsense —
        // there is no refresh token yet (user is logging in!), so the
        // refresh attempt fails with "No refresh token available", that
        // gets misclassified as a network error in doAtomicRefresh, and the
        // LoginForm sees a generic error instead of the real "Invalid
        // credentials" message. Mirror the request-interceptor's
        // isPublicAuthEndpoint check (line ~286) so the real 401 bubbles up.
        const publicAuthEndpoint = isPublicAuthEndpoint(originalRequest.url);

        if (error.response?.status === 431 && originalRequest.headers?.Authorization) {
          triggerAuthFailure();
          if (window.location.pathname !== '/login') {
            window.location.href = '/login?reason=session_invalid';
          }
          return Promise.reject(error);
        }

        // Handle 401 Unauthorized — reactive fallback (proactive should prevent most of these)
        if (error.response?.status === 401 && !originalRequest._retry && !publicAuthEndpoint) {
          originalRequest._retry = true;

          try {
            // Atomic mutex ensures only one refresh call even with concurrent 401s
            const newToken = await this.doAtomicRefresh();

            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
            }

            return this.client(originalRequest);
          } catch (refreshError) {
            // doAtomicRefresh already handles auth failure / redirect
            return Promise.reject(refreshError);
          }
        }

        // For network errors, don't trigger logout - just reject
        if (!error.response) {
          console.warn('Network error - server may be unavailable');
        }

        return Promise.reject(error);
      },
    );
  }

  // HTTP Methods
  async get<T>(url: string, config = {}) {
    const response = await this.client.get<T>(url, config);
    return response.data;
  }

  async post<T>(url: string, data = {}, config = {}) {
    const response = await this.client.post<T>(url, data, config);
    return response.data;
  }

  async put<T>(url: string, data = {}, config = {}) {
    const response = await this.client.put<T>(url, data, config);
    return response.data;
  }

  async patch<T>(url: string, data = {}, config = {}) {
    const response = await this.client.patch<T>(url, data, config);
    return response.data;
  }

  async delete<T>(url: string, config = {}) {
    const response = await this.client.delete<T>(url, config);
    return response.data;
  }

  // Public method to request a token refresh (for external callers)
  public requestRefresh(): Promise<string> {
    return this.doAtomicRefresh();
  }

  // Get raw axios instance for special cases
  getAxiosInstance(): AxiosInstance {
    return this.client;
  }
}

// Export singleton instance
export const apiClient = new ApiClient();
export default apiClient;

// Public refresh trigger — allows external code to request a token refresh
export const doRefresh = (): Promise<string> => apiClient.requestRefresh();

// Raw axios instance — for legacy code that expects AxiosResponse (with .data unwrapping)
export const getAxiosInstance = (): import('axios').AxiosInstance => apiClient.getAxiosInstance();
